import { z } from 'zod'
import { Model } from './architecture'
import { Currency } from './Currency'
import { Market } from '../shared/domain/Market'
import { DomainError } from './DomainError'
import { ErrorsCode } from './ErrorsCode'

export const MoneyValidation = z.object({
  amount: z.number({ required_error: '[Money] amount is required' }).nonnegative(),
  currency: z.instanceof(Currency),
})

export class Money extends Model {
  static readonly #MIN_PRICE_DIGITS = 0
  static readonly #MAX_PRICE_DIGITS = 2

  static create({ amount, currency }: z.infer<typeof MoneyValidation>) {
    MoneyValidation.parse({ amount, currency })

    return new Money(amount, currency)
  }

  static fromJSON(json: ReturnType<Money['toJSON']>) {
    return new Money(json.amount, Currency.create({ value: json.currency }))
  }

  static fromJSONOrUndefined(json?: ReturnType<Money['toJSON']>) {
    if (!json?.amount || !json?.currency) return undefined

    try {
      return new Money(json.amount, Currency.create({ value: json.currency }))
    } catch {
      return undefined
    }
  }

  static createOrUndefined({
    amount,
    currency,
  }: {
    amount: number | undefined
    currency: Currency | undefined
  }) {
    if (!amount || !currency) return undefined

    return Money.create({ amount, currency })
  }

  constructor(
    private readonly _amount: number,
    private readonly _currency: Currency
  ) {
    super()
  }

  get amount() { return this._amount } // prettier-ignore
  get currency() { return this._currency.value } // prettier-ignore

  toJSON() {
    return { amount: this.amount, currency: this.currency }
  }

  toFormattedString(locale: string, market: Market) {
    const amount = Math.round(this.amount)

    if (!market.isFilled()) throw DomainError.withCodes(ErrorsCode.MISSING_MARKET)

    if (
      market.isOneOf(['au', 'ca', 'nz', 'sg']) &&
      (this.currency === 'AUD' ||
        this.currency === 'CAD' ||
        this.currency === 'NZD' ||
        this.currency === 'SGD')
    ) {
      const price = Intl.NumberFormat(locale, {
        style: 'currency',
        currency: this.currency,
        currencyDisplay: 'symbol',
        minimumFractionDigits: Money.#MIN_PRICE_DIGITS,
        maximumFractionDigits: Money.#MAX_PRICE_DIGITS,
      }).format(amount)

      const prefix = {
        ca: 'CA$',
        au: 'AU$',
        nz: 'NZ$',
        sg: 'S$',
      }

      return price.replace(/[AC]?\$/, prefix[market.value])
    }

    if (market.is('ro') && this.currency === 'RON') {
      const price = Intl.NumberFormat(locale, {
        style: 'currency',
        currency: this.currency,
        currencyDisplay: 'name',
        minimumFractionDigits: Money.#MIN_PRICE_DIGITS,
        maximumFractionDigits: Money.#MAX_PRICE_DIGITS,
      }).format(amount)

      return price.replace(/(\s)?(de)?\slei\sromânești/gm, '\u00a0lei')
    }

    if (market.is('no') && this.currency === 'NOK') {
      const price = Intl.NumberFormat(locale, {
        style: 'currency',
        currency: this.currency,
        currencyDisplay: 'symbol',
        minimumFractionDigits: Money.#MIN_PRICE_DIGITS,
        maximumFractionDigits: Money.#MAX_PRICE_DIGITS,
      }).format(amount)

      return price.replace('kr', this.currency)
    }

    if (market.is('mx') && this.currency === 'MXN') {
      const price = Intl.NumberFormat(locale, {
        style: 'currency',
        currency: this.currency,
        currencyDisplay: 'symbol',
        minimumFractionDigits: Money.#MIN_PRICE_DIGITS,
        maximumFractionDigits: Money.#MAX_PRICE_DIGITS,
      }).format(amount)

      return price
    }

    return Intl.NumberFormat(locale, {
      style: 'currency',
      currency: this.currency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: Money.#MIN_PRICE_DIGITS,
      maximumFractionDigits: Money.#MAX_PRICE_DIGITS,
    }).format(amount)
  }

  getSymbol(locale: string, market: Market = Market.create({ value: 'us' })) {
    const parts = Intl.NumberFormat(locale, {
      style: 'currency',
      currency: this.currency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: Money.#MIN_PRICE_DIGITS,
      maximumFractionDigits: Money.#MAX_PRICE_DIGITS,
    }).formatToParts()

    const symbol = parts.find(part => part.type === 'currency')?.value ?? '$'

    if (market.is('au')) {
      return symbol.replace(/(A|AU)?\$/, 'AU$')
    }

    if (market.is('ca')) {
      return symbol.replace(/(CA)?\$/, 'CA$')
    }

    if (market.is('no')) {
      return symbol.replace(/kr/, this.currency)
    }

    return symbol
  }
}
